import { Record as record, List, Map } from 'immutable';
import _ from 'lodash';

import Fare from './Fare';
import { toImmutable } from '../helpers/Immutable';
import CheapestFares from './CheapestFares';
import { FareClasses, JourneyDirections } from '../constants/AppConstants';
import { construct, resolve, resolveAll } from '../helpers/LinkMapper';
import { without } from '../helpers/Immutable';

const defaults = {
    id: null,
    singles: new List(),
    returns: new List(),
    cheapest: new Map()
};


class Fares extends record(defaults) {

    static fromJSON (json = {}) {
        return construct(Fares, json, {
            singles: resolveAll.as(Fare),
            returns: resolveAll.as(Fare),
            cheapest: resolve.as(CheapestFares)
        });
    }

    static isStandard (fare) {
        return fare.isStandard();
    }

    static isFirstClass (fare) {
        return fare.isFirstClass();
    }

    static isCarnet (fare) {
        return fare.isCarnet();
    }

    static isOffPeak (fare) {
        return fare.isOffPeak();
    }

    static isAdvance (fare) {
        return fare.isAdvance();
    }

    static isAnytime (fare) {
        return fare.isAnytime();
    }

    static isSingle (fare) {
        return !fare.isReturn();
    }

    static isReturn (fare) {
        return fare.isReturn();
    }

    static isTravelcard (fare) {
        return fare.ticketType.isReturnTravelcard();
    }

    static isSingleOrTravelcard (fare) {
        return !fare.isReturn() || fare.ticketType.isReturnTravelcard();
    }

    getStandard () {
        return new Map({
            singles: this.singles.filter(Fares.isStandard).filterNot(Fares.isCarnet),
            returns: this.returns.filter(Fares.isStandard).filterNot(Fares.isCarnet)
        });
    }

    getFirstClass () {
        return new Map({
            singles: this.singles.filter(Fares.isFirstClass).filterNot(Fares.isCarnet),
            returns: this.returns.filter(Fares.isFirstClass).filterNot(Fares.isCarnet)
        });
    }

    getCarnets () {
        return toImmutable([ this.singles, this.returns ]).flatten(true).filter(Fares.isCarnet);
    }

    getByClass () {
        return {
            [ FareClasses.STANDARD ]: this.getStandard(),
            [ FareClasses.FIRST_CLASS ]: this.getFirstClass(),
            [ FareClasses.CARNET ]: this.getCarnets()
        };
    }

    static getCheapestFromList (list) {
        return list.sortBy(fare => fare.totalPrice).first();
    }

    getClosestPriceFromFares (fares, price) {
        const sortedByLowestPrice = fares.sortBy(fare => fare.totalPrice);
        const sortedByHighestPrice = sortedByLowestPrice.reverse();

        const fareWithMatchingOrLowerTicketPrice = sortedByLowestPrice.find(fare => fare.hasMatchingOrLowerTicketPrice(price));
        const fareWithMatchingOrHigherTicketPrice = sortedByHighestPrice.find(fare => fare.hasMatchingOrHigherTicketPrice(price));

        return fareWithMatchingOrLowerTicketPrice || fareWithMatchingOrHigherTicketPrice;
    }

    getClosestPriceListFromFares (fares, price, ticketType) {
        const sortedByPrice = fares.sortBy(fare => fare.totalPrice);

        const sameTicketType = sortedByPrice.filter(fare => fare.ticketType.code === ticketType);

        const firstClassFares = fares.filter(fare => fare.ticketType.isFirstClass);
        const firstClassFare = this.getClosestPriceFromFares(firstClassFares, price);

        if (sameTicketType.size && firstClassFare) {
            // to avoid displaying 1st class ticket twice if original order is 1st class
            return firstClassFare.ticketType.code === ticketType
            ? sameTicketType
            : sameTicketType.push(firstClassFare);
        }

        const standardClassFares = fares.filter(fare => !fare.ticketType.isFirstClass);
        const standardClassFare = this.getClosestPriceFromFares(standardClassFares, price);

        return List.of(standardClassFare, firstClassFare).filter(f => f);
    }

    static getCheapestCompatibleWithServiceFromList (list, service) {
        const orderedList = list.filter((fare)=>{
            return service.isValidWithFare(fare);
        }).sortBy(fare => fare.totalPrice);

        const first = orderedList.first();

        const cheapestList = orderedList.filter(fare => fare.totalPrice === first.totalPrice);
        if (service.fares.cheapest.return && cheapestList.find(fare => fare.id === service.fares.cheapest.return.id)) {
            return service.fares.cheapest.return;
        }
        return first;
    }

    getSingleFares (includeTravelcards = false) {
        if (includeTravelcards) {
            return this.singles.concat(this.returns.filter(Fares.isTravelcard));
        }
        return this.singles;
    }

    getReturnFares () {
        return this.returns;
    }

    getAllFares () {
        return this.singles.concat(this.returns);
    }

    getCheapestFare (filters = []) {
        let fares = this.getAllFares();
        filters.map((filter)=> {
            if (typeof filter === 'function') {
                fares = fares.filter(filter);
            }
        });

        return Fares.getCheapestFromList(fares);
    }

    getMatchingOrClosestFarePrice (price) {
        const fares = this.getAllFares();
        const fareWithClosestPrice = this.getClosestPriceFromFares(fares, price);

        return fareWithClosestPrice && fareWithClosestPrice.totalPrice;
    }

    getCheapestOutwardSingle () {
        return this.getIn([ 'cheapest', 'outwardSingle' ]);
    }

    getCheapestReturn () {
        return this.getIn([ 'cheapest', 'return' ]);
    }

    getCheapestReturnPrice () {
        return _.result(this.getCheapestReturn(), 'getPrice');
    }

    getCheapestSinglesPrice () {
        return _.sum([ this.getCheapestOutwardSinglePrice(), this.getCheapestInwardSinglePrice() ]);
    }

    getCheapestOutwardSinglePrice () {
        return _.result(this.getCheapestOutwardSingle(), 'getPrice');
    }

    getCheapestInwardSingle () {
        return this.getIn([ 'cheapest', 'inboundSingle' ]);
    }

    getCheapestInwardSinglePrice () {
        return _.result(this.getCheapestInwardSingle(), 'getPrice');
    }

    getCheapestTotalPrice () {
        return this.getIn([ 'cheapest', 'totalPrice' ]);
    }

    getCheapestOutwardAny () {
        const singlesPrice = this.getCheapestSinglesPrice() || Infinity;
        const returnsPrice = this.getCheapestReturnPrice() || Infinity;

        if (singlesPrice < returnsPrice) {
            return this.getCheapestOutwardSingle();
        }

        return this.getCheapestReturn();
    }

    getCheapestForDirection (direction) {
        return {
            [ JourneyDirections.OUTWARD ]: this.getCheapestOutwardAny,
            [ JourneyDirections.INWARD ]: this.getCheapestInwardSingle
        }[direction].apply(this);
    }

    without (fare) {
        if (!fare) {
            return this;
        }
        return this.withMutations((fares) => {
            return fares
                .set('singles', without(fares.get('singles'), fare, 'id'))
                .set('returns', without(fares.get('returns'), fare, 'id'));
        });
    }

    filteredByCategory (category) {
        return this.withMutations((fares) => {
            return fares.merge({
                singles: fares.singles.filter((x) => x.category === category),
                returns: fares.returns.filter((x) => x.category === category)
            });
        });
    }

    filteredByRoute (route, price, ticketType) {
        return this.withMutations((fares) => {
            const singles = fares.singles.filter((x) => x.route.code === route.code);
            const returns = fares.returns.filter((x) => x.route.code === route.code);
            return fares.merge({
                singles: this.getClosestPriceListFromFares(singles, price, ticketType),
                returns: this.getClosestPriceListFromFares(returns, price, ticketType)
            });
        });
    }

    hasStandard () {
        return !!this.getStandard().flatten(1).toSet().size;
    }

    hasFirstClass () {
        return !!this.getFirstClass().flatten(1).toSet().size;
    }

}


export default Fares;



// WEBPACK FOOTER //
// ./src/records/Fares.js